import { useEffect } from 'react';
import { useWriteContract, useWaitForTransactionReceipt } from 'wagmi';
import { parseEther, type Address } from 'viem';
import { ERC20_ABI, STAKING_ABI } from '../constants';

interface UseStakingActionsParams {
  farToken: Address;
  stakingContract: Address;
  setStakingStep: (step: 'idle' | 'approving' | 'staking' | 'claiming' | 'unstaking' | 'extending') => void;
  setErrorMessage: (message: string | null) => void;
  setStakeAmount: (amount: string) => void;
  setSelectedStakeId: (id: number | null) => void;
  refetchBalance: () => void;
  refetchAllowance: () => void;
  refetchPositions: () => void;
  refetchEnhancedStats: () => void;
  refetchUserDashboard: () => void;
  refetchTotalPendingRewards: () => void;
}

/**
 * Custom hook for all staking write operations and handlers
 */
export const useStakingActions = ({
  farToken,
  stakingContract,
  setStakingStep,
  setErrorMessage,
  setStakeAmount,
  setSelectedStakeId,
  refetchBalance,
  refetchAllowance,
  refetchPositions,
  refetchEnhancedStats,
  refetchUserDashboard,
  refetchTotalPendingRewards,
}: UseStakingActionsParams) => {
  // Write contract hooks
  const {
    writeContract: writeApprove,
    data: approveHash,
    isPending: isApprovePending,
    error: approveError,
    isError: isApproveError,
  } = useWriteContract();

  const {
    writeContract: writeStake,
    data: stakeHash,
    isPending: isStakePending,
    error: stakeError,
    isError: isStakeError,
  } = useWriteContract();

  const {
    writeContract: writeClaim,
    data: claimHash,
    isPending: isClaimPending,
    error: claimError,
    isError: isClaimError,
  } = useWriteContract();

  const {
    writeContract: writeUnstake,
    data: unstakeHash,
    isPending: isUnstakePending,
    error: unstakeError,
    isError: isUnstakeError,
  } = useWriteContract();

  const {
    writeContract: writeExtendLock,
    data: extendLockHash,
    isPending: isExtendLockPending,
    error: extendLockError,
    isError: isExtendLockError,
  } = useWriteContract();

  // Wait for transactions
  const {
    isLoading: isApproveLoading,
    isSuccess: isApproveSuccess,
    error: approveTxError,
    isError: isApproveTxError,
  } = useWaitForTransactionReceipt({
    hash: approveHash,
  });

  const {
    isLoading: isStakeLoading,
    isSuccess: isStakeSuccess,
    error: stakeTxError,
    isError: isStakeTxError,
  } = useWaitForTransactionReceipt({
    hash: stakeHash,
  });

  const {
    isLoading: isClaimLoading,
    isSuccess: isClaimSuccess,
    error: claimTxError,
    isError: isClaimTxError,
  } = useWaitForTransactionReceipt({
    hash: claimHash,
  });

  const {
    isLoading: isUnstakeLoading,
    isSuccess: isUnstakeSuccess,
    error: unstakeTxError,
    isError: isUnstakeTxError,
  } = useWaitForTransactionReceipt({
    hash: unstakeHash,
  });

  const {
    isLoading: isExtendLockLoading,
    isSuccess: isExtendLockSuccess,
    error: extendLockTxError,
    isError: isExtendLockTxError,
  } = useWaitForTransactionReceipt({
    hash: extendLockHash,
  });

  // Auto-refresh after successful transactions
  useEffect(() => {
    if (isApproveSuccess) {
      refetchAllowance();
      setStakingStep('idle');
      setErrorMessage(null);
    }
  }, [isApproveSuccess, refetchAllowance, setStakingStep, setErrorMessage]);

  useEffect(() => {
    if (isStakeSuccess) {
      refetchBalance();
      refetchPositions();
      refetchEnhancedStats();
      refetchUserDashboard();
      refetchTotalPendingRewards();
      setStakingStep('idle');
      setStakeAmount('');
      setErrorMessage(null);
    }
  }, [isStakeSuccess, refetchBalance, refetchPositions, refetchEnhancedStats, refetchUserDashboard, refetchTotalPendingRewards, setStakingStep, setStakeAmount, setErrorMessage]);

  useEffect(() => {
    if (isClaimSuccess) {
      refetchBalance();
      refetchTotalPendingRewards();
      refetchUserDashboard();
      setStakingStep('idle');
      setErrorMessage(null);
    }
  }, [isClaimSuccess, refetchBalance, refetchTotalPendingRewards, refetchUserDashboard, setStakingStep, setErrorMessage]);

  useEffect(() => {
    if (isUnstakeSuccess) {
      refetchBalance();
      refetchPositions();
      refetchEnhancedStats();
      refetchUserDashboard();
      refetchTotalPendingRewards();
      setStakingStep('idle');
      setSelectedStakeId(null);
      setErrorMessage(null);
    }
  }, [isUnstakeSuccess, refetchBalance, refetchPositions, refetchEnhancedStats, refetchUserDashboard, refetchTotalPendingRewards, setStakingStep, setSelectedStakeId, setErrorMessage]);

  // Handle transaction errors and rejections
  useEffect(() => {
    if (isApproveError || isApproveTxError) {
      const error = approveError || approveTxError;
      console.error('Approve error:', error);
      const errorMsg = error?.message?.includes('User rejected') || error?.message?.includes('User denied')
        ? 'Transaction rejected by user'
        : 'Failed to approve tokens. Please try again.';
      setErrorMessage(errorMsg);
      setStakingStep('idle');
    }
  }, [isApproveError, isApproveTxError, approveError, approveTxError, setErrorMessage, setStakingStep]);

  useEffect(() => {
    if (isStakeError || isStakeTxError) {
      const error = stakeError || stakeTxError;
      console.error('Stake error:', error);
      const errorMsg = error?.message?.includes('User rejected') || error?.message?.includes('User denied')
        ? 'Transaction rejected by user'
        : 'Failed to stake tokens. Please try again.';
      setErrorMessage(errorMsg);
      setStakingStep('idle');
    }
  }, [isStakeError, isStakeTxError, stakeError, stakeTxError, setErrorMessage, setStakingStep]);

  useEffect(() => {
    if (isClaimError || isClaimTxError) {
      const error = claimError || claimTxError;
      console.error('Claim error:', error);
      const errorMsg = error?.message?.includes('User rejected') || error?.message?.includes('User denied')
        ? 'Transaction rejected by user'
        : 'Failed to claim rewards. Please try again.';
      setErrorMessage(errorMsg);
      setStakingStep('idle');
    }
  }, [isClaimError, isClaimTxError, claimError, claimTxError, setErrorMessage, setStakingStep]);

  useEffect(() => {
    if (isUnstakeError || isUnstakeTxError) {
      const error = unstakeError || unstakeTxError;
      console.error('Unstake error:', error);
      const errorMsg = error?.message?.includes('User rejected') || error?.message?.includes('User denied')
        ? 'Transaction rejected by user'
        : 'Failed to withdraw tokens. Please try again.';
      setErrorMessage(errorMsg);
      setStakingStep('idle');
      setSelectedStakeId(null);
    }
  }, [isUnstakeError, isUnstakeTxError, unstakeError, unstakeTxError, setErrorMessage, setStakingStep, setSelectedStakeId]);

  useEffect(() => {
    if (isExtendLockSuccess) {
      refetchPositions();
      refetchEnhancedStats();
      refetchUserDashboard();
      refetchTotalPendingRewards();
      setStakingStep('idle');
      setSelectedStakeId(null);
      setErrorMessage(null);
    }
  }, [isExtendLockSuccess, refetchPositions, refetchEnhancedStats, refetchUserDashboard, refetchTotalPendingRewards, setStakingStep, setSelectedStakeId, setErrorMessage]);

  useEffect(() => {
    if (isExtendLockError || isExtendLockTxError) {
      const error = extendLockError || extendLockTxError;
      console.error('Extend lock error:', error);
      const errorMsg = error?.message?.includes('User rejected') || error?.message?.includes('User denied')
        ? 'Transaction rejected by user'
        : 'Failed to extend lock period. Please try again.';
      setErrorMessage(errorMsg);
      setStakingStep('idle');
      setSelectedStakeId(null);
    }
  }, [isExtendLockError, isExtendLockTxError, extendLockError, extendLockTxError, setErrorMessage, setStakingStep, setSelectedStakeId]);

  // Handler functions
  const handleApprove = async (stakeAmount: string) => {
    if (!stakeAmount) return;

    setStakingStep('approving');

    try {
      const amount = parseEther(stakeAmount);
      writeApprove({
        address: farToken,
        abi: ERC20_ABI,
        functionName: 'approve',
        args: [stakingContract, amount],
      });
    } catch (error) {
      setStakingStep('idle');
    }
  };

  const handleStake = async (stakeAmount: string, lockDays: number) => {
    if (!stakeAmount) return;

    setStakingStep('staking');

    try {
      const amount = parseEther(stakeAmount);
      // Calculate unlock time: current timestamp + lock duration
      const currentTime = Math.floor(Date.now() / 1000);
      const lockDuration = lockDays * 24 * 60 * 60; // Convert days to seconds
      const unlockTime = BigInt(currentTime + lockDuration);

      writeStake({
        address: stakingContract,
        abi: STAKING_ABI,
        functionName: 'createPosition',
        args: [amount, unlockTime],
      });
    } catch (error) {
      setStakingStep('idle');
    }
  };

  const handleClaim = async () => {
    setStakingStep('claiming');

    try {
      writeClaim({
        address: stakingContract,
        abi: STAKING_ABI,
        functionName: 'claimAllRewards',
      });
    } catch (error) {
      setStakingStep('idle');
    }
  };

  const handleWithdraw = async (positionId: number, amount: bigint) => {
    setStakingStep('unstaking');
    setSelectedStakeId(positionId);

    try {
      writeUnstake({
        address: stakingContract,
        abi: STAKING_ABI,
        functionName: 'withdraw',
        args: [BigInt(positionId), amount],
      });
    } catch (error) {
      setStakingStep('idle');
      setSelectedStakeId(null);
    }
  };

  const handleExtendLock = async (positionId: number, additionalDays: number) => {
    setStakingStep('extending');
    setSelectedStakeId(positionId);

    try {
      // Calculate new unlock time: current timestamp + additional lock duration
      const currentTime = Math.floor(Date.now() / 1000);
      const lockDuration = additionalDays * 24 * 60 * 60; // Convert days to seconds
      const newUnlockTime = BigInt(currentTime + lockDuration);

      writeExtendLock({
        address: stakingContract,
        abi: STAKING_ABI,
        functionName: 'extendLock',
        args: [BigInt(positionId), newUnlockTime],
      });
    } catch (error) {
      setStakingStep('idle');
      setSelectedStakeId(null);
    }
  };

  return {
    // Transaction state
    isApprovePending,
    isApproveLoading,
    isStakePending,
    isStakeLoading,
    isClaimPending,
    isClaimLoading,
    isUnstakePending,
    isUnstakeLoading,
    isExtendLockPending,
    isExtendLockLoading,

    // Handler functions
    handleApprove,
    handleStake,
    handleClaim,
    handleWithdraw,
    handleExtendLock,
  };
};
